import * as Yup from 'yup';
import { AppSettings } from 'settings/AppSettings';
import { Button, FormTextField, Typography } from 'omakase';
import {
  ElementProvider,
  NewCardPaymentElement,
  useNewCardPaymentElement,
} from '@tock/payment-elements';
import { Form, Formik } from 'formik';
import { LoadingContainer } from 'ui';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import {
  cardsContainer,
  formContainer,
  formContainerElement,
  mobileHeading,
  submitButton,
} from 'routes/protected/account/paymentMethodsRouteCssDefs';
import { core } from '@tock/coretypes';
import { getAppPatron } from 'selectors/appSelector';
import { getDeviceData } from 'selectors/paymentCardSelectors';
import { safeToNumber } from '@tock/coretypes/src/protoUtils';
import { useAppSettings } from 'selectors/settingsSelector';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import AccountPageMobileHeading from 'components/account/AccountPageMobileHeading';
import AddCreditCardForm from 'forms/AddCreditCardForm';
import ConsumerPanel from 'components/ConsumerPanel';
import CreditCards from 'components/credit-cards/CreditCards';
import FeatureFlag, { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import TurnstileValidator, { isTurnstileActive } from 'forms/TurnstileValidator';

export const PaymentMethodsRoute = () => {
  const { actions } = useActions();
  const { cards, listRequestInProgress } = useSelector((state: StoreType) => state.paymentCard);
  useMountEffect(() => {
    actions.paymentCard.fetchAll();
  });

  const isStripeElementEnabled = useFeatureFlag('STRIPE_CARD_ELEMENT_GUEST_PROFILE');

  const handleDeleteCard = (card: core.PaymentCard) => {
    actions.paymentCard
      .remove(card)
      .then(() => actions.paymentCard.fetchAll())
      .catch((resp) => {
        if (safeToNumber(resp.error.errorCode) === 3052) {
          actions.toast.showError(resp.error.message);
        } else {
          throw resp;
        }
      });
  };

  return (
    <div className="Account-content">
      <AccountPageMobileHeading css={mobileHeading}>Payment methods</AccountPageMobileHeading>
      <div css={cardsContainer}>
        <LoadingContainer loading={listRequestInProgress}>
          <CreditCards cards={cards} onDelete={handleDeleteCard} />
          {cards.length === 0 && <Typography>You have no saved payment methods.</Typography>}
        </LoadingContainer>
      </div>
      {isStripeElementEnabled ? <HostedPaymentFieldsWrapper /> : <SelfHostedPaymentFields />}
    </div>
  );
};

const HostedPaymentFieldsWrapper = () => {
  const { STRIPE_PUBLIC_KEY }: AppSettings = useAppSettings();
  return (
    <ElementProvider stripePublicKey={STRIPE_PUBLIC_KEY}>
      <HostedPaymentFields />
    </ElementProvider>
  );
};

interface ExternalCardFields {
  name: string;
}

const externalCardFieldsSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
});

const HostedPaymentFields = () => {
  const { actions } = useActions();
  const me = useSelector(getAppPatron);
  const deviceData = useSelector(getDeviceData);
  useMountEffect(() => {
    actions.paymentCard.collectDeviceData();
  });

  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);

  const [challengeResponse, setChallengeResponse] = useState<string>();
  const isTurnstileFlagEnabled = useFeatureFlag('TURNSTILE_RENDER_CLIENT');
  const includeTurnstileValidation = isTurnstileActive(isTurnstileFlagEnabled);
  const showBillingAddressFields = useFeatureFlag('STRIPE_AMEX_FULL_BILLING_ADDRESS');

  const { tokenizePaymentCard, reset } = useNewCardPaymentElement();

  const handleSubmit = async ({ name }: ExternalCardFields) => {
    setIsSubmitInProgress(true);

    const tokenizedCard = await tokenizePaymentCard({ name });
    const card = {
      ...tokenizedCard,
      deviceData,
    };
    await actions.paymentCard.saveCardToTock(card, {
      requireTurnstile: includeTurnstileValidation,
      challengeResponse,
    });
    actions.paymentCard.fetchAll();
    reset();
    setIsSubmitInProgress(false);
  };
  return (
    <div css={formContainerElement}>
      <Typography variant="heading04" mb={2}>
        Add a payment method
      </Typography>

      <Formik<ExternalCardFields>
        initialValues={{ name: `${me?.firstName} ${me?.lastName}` }}
        validationSchema={externalCardFieldsSchema}
        onSubmit={handleSubmit}
      >
        <Form data-testid="credit-card-form">
          {!showBillingAddressFields && (
            <FormTextField
              required
              label="Name"
              name="name"
              placeholder="Name on card"
              variant="inverse"
            />
          )}
          <NewCardPaymentElement
            callSite="consumer_profile"
            showAddressFields={showBillingAddressFields}
          />
          <Button
            css={submitButton}
            type="submit"
            size="large"
            fullWidth
            variant="primary"
            disabled={isSubmitInProgress}
            data-testid="save-card-button"
          >
            Save card
          </Button>
          {includeTurnstileValidation && (
            <TurnstileValidator
              onReceiveChallengeResponse={(challengeResponse) => {
                setChallengeResponse(challengeResponse);
              }}
            />
          )}
        </Form>
      </Formik>
    </div>
  );
};

/**
 * Old deprecated self-hosted fields
 */
const SelfHostedPaymentFields = () => {
  const { actions } = useActions();
  const { insertRequestInProgress, tokenizeRequestInProgress } = useSelector(
    (state: StoreType) => state.paymentCard
  );
  const handleCardCreated = () => {
    actions.paymentCard.fetchAll();
  };
  return (
    <>
      <FeatureFlag doNotShow ifEnabled="BLOCK_PROFILE_ADD_NEW_PAYMENT">
        <div css={formContainer}>
          <LoadingContainer loading={tokenizeRequestInProgress || insertRequestInProgress}>
            <AddCreditCardForm onCardCreated={handleCardCreated} />
          </LoadingContainer>
        </div>
      </FeatureFlag>
      <FeatureFlag ifEnabled="BLOCK_PROFILE_ADD_NEW_PAYMENT">
        <ConsumerPanel>
          <Typography>Add new payments when booking</Typography>
        </ConsumerPanel>
      </FeatureFlag>
    </>
  );
};
